import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  User, 
  Mail, 
  Lock, 
  Save, 
  Eye, 
  EyeOff, 
  AlertCircle,
  CheckCircle,
  ArrowLeft,
  HardDrive,
  Database,
  Zap,
  Activity,
  TrendingUp,
  BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import usageControlService from '../../services/usageControlService';
import RoleSelectorModal from '../../components/Navigation/RoleSelectorModal';
import GoogleCalendarSettings from '../../components/GoogleCalendarSettings/GoogleCalendarSettings';
const UserProfile = () => {
  const { t } = useTranslation();
  const { user, updateProfile, changePassword } = useAuth();
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: ''
  });
  
  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // UI state
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  
  // Usage tracking state
  const [usageData, setUsageData] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showUsage, setShowUsage] = useState(false);
  
  // Role selector modal state
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      // Debug: Log user data to see what we're getting
      console.log('🔍 USER_PROFILE_DEBUG: User data received:', {
        userId: user.userId,
        tenantUserId: user.tenantUserId,
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        tenantCode: user.tenantCode,
        tenantName: user.tenantName,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        lastLoginAt: user.lastLoginAt,
        isActive: user.isActive,
        phone: user.phone,
        department: user.department,
        jobTitle: user.jobTitle,
        userType: user.userType,
        roles: user.roles,
        allFields: Object.keys(user)
      });
      
      // Debug timestamp fields specifically
      console.log('🔍 TIMESTAMP_DEBUG:', {
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        lastLoginAt: user.lastLoginAt,
        createdAtType: typeof user.createdAt,
        updatedAtType: typeof user.updatedAt,
        lastLoginAtType: typeof user.lastLoginAt,
        createdAtValue: user.createdAt,
        updatedAtValue: user.updatedAt,
        lastLoginAtValue: user.lastLoginAt
      });
      
      setProfileForm({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        username: user.username || ''
      });
    }
  }, [user]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateProfileForm = () => {
    const newErrors = {};
    
    if (!profileForm.firstName.trim()) {
      newErrors.firstName = t('auth.firstNameRequired');
    }
    
    if (!profileForm.lastName.trim()) {
      newErrors.lastName = t('auth.lastNameRequired');
    }
    
    // Email and username validation removed since they're read-only
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    
    if (!passwordForm.currentPassword) {
      newErrors.currentPassword = t('auth.currentPasswordRequired');
    }
    
    if (!passwordForm.newPassword) {
      newErrors.newPassword = t('auth.newPasswordRequired');
    } else if (passwordForm.newPassword.length < 8) {
      newErrors.newPassword = t('auth.newPasswordMinLength');
    } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(passwordForm.newPassword)) {
      newErrors.newPassword = t('auth.newPasswordComplexity');
    }
    
    if (!passwordForm.confirmPassword) {
      newErrors.confirmPassword = t('auth.confirmPasswordRequired');
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch');
    }
    
    setPasswordErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateProfileForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      // Only send fields that the backend accepts (exclude username and email)
      const { username, email, ...profileData } = profileForm;
      const result = await updateProfile(profileData);
      if (result.success) {
        setSuccessMessage(t('auth.profileUpdated'));
      }
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }
    
    setIsPasswordLoading(true);
    setPasswordErrors({});
    
    try {
      const result = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });
      
      if (result.success) {
        setSuccessMessage(t('auth.passwordChanged'));
        // Clear password form
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Password change error:', error);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const getUserRoles = () => {
    // For admin users, role is stored as a string in user.role
    if (user?.userType === 'admin' && user?.role) {
      return user.role;
    }
    
    // For regular users, check if there's a selected role first
    const selectedRole = user?.selectedRole?.RoleName || user?.selectedRole;
    if (selectedRole) {
      // Helper function to format role name
      const formatRoleName = (roleName) => {
        return roleName
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, str => str.toUpperCase())
          .trim();
      };
      return formatRoleName(selectedRole);
    }
    
    // Fallback: For regular users, roles are stored as an array in user.roles
    if (!user?.roles || user.roles.length === 0) return t('auth.noRolesAssigned');
    return user.roles.map(role => role.RoleName || role).join(', ');
  };

  const handleTestCentralDb = async () => {
    try {
      const response = await fetch('/api/auth/test-central-db', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      
      console.log('🔍 CENTRAL_DB_TEST_RESULT:', result);
      
      if (result.success) {
        setSuccessMessage(`Central DB Test: ${result.message}`);
        if (result.centralUser) {
          console.log('✅ Central user found:', result.centralUser);
        } else {
          console.log('❌ Central user not found');
        }
      } else {
        setSuccessMessage(`Central DB Test Failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Central DB test error:', error);
      setSuccessMessage('Central DB Test Failed: ' + error.message);
    }
  };

  const loadUsageData = async () => {
    if (!user?.tenantCode) return;
    
    try {
      setUsageLoading(true);
      const response = await usageControlService.getTenantUsage(user.tenantCode);
      
      // Handle new backend format (data.data wrapper)
      const usage = response.data || response;
      
      // Define entity display orders
      const storageEntityOrder = [
        { key: 'Customer', label: 'Customer' },
        { key: 'Lead', label: 'Lead' },
        { key: 'Opportunity', label: 'Opportunity' },
        { key: 'Activity', label: 'Activity' },
        { key: 'SalesNote', label: 'Notes' },
        { key: 'Knowledge', label: 'Knowledge Base' }
      ];

      const entityRecordOrder = [
        { key: 'Customers', label: 'Customers' },
        { key: 'Leads', label: 'Leads' },
        { key: 'Opportunities', label: 'Opportunities' },
        { key: 'Activities', label: 'Activities' },
        { key: 'SalesNotes', label: 'Notes' },
        { key: 'SupportTickets', label: 'Support Tickets' },
        { key: 'Knowledge', label: 'Knowledge Base' },
        { key: 'Users', label: 'Users' }
      ];

      const rawStorageByEntity = usage.storage?.byEntityType || usage.storage?.byEntity || {};
      const rawEntityCounts = usage.entities?.byType || usage.entities || {};

      const normalizedUsage = {
        ...usage,
        storage: {
          totalSize: usage.storage?.total || usage.storage?.totalSize || 0,
          totalFiles: usage.storage?.fileCount || usage.storage?.totalFiles || 0,
          byEntity: {},
          order: storageEntityOrder,
          list: []
        },
        entities: {
          total: usage.entities?.total || 0,
          byType: {},
          order: entityRecordOrder,
          list: []
        },
        aiTokens: {
          monthlyTokens: usage.aiTokens?.monthly || usage.aiTokens?.monthlyTokens || 0,
          dailyTokens: usage.aiTokens?.daily || usage.aiTokens?.dailyTokens || 0,
          totalRequests: usage.aiTokens?.requests || usage.aiTokens?.totalRequests || 0,
          byService: usage.aiTokens?.byService || {}
        },
        limits: {
          storage: {
            maxTotalSize: usage.limits?.storage?.total?.limit || usage.limits?.storage?.maxTotalSize || 10737418240,
            byEntityType: usage.limits?.storage?.byEntityType || {}
          },
          entities: usage.limits?.entities || {},
          aiTokens: {
            monthlyLimit: usage.limits?.aiTokens?.monthly?.limit || usage.limits?.aiTokens?.monthlyLimit || 1000000,
            dailyLimit: usage.limits?.aiTokens?.daily?.limit || usage.limits?.aiTokens?.dailyLimit || 50000
          }
        },
        alerts: usage.alerts || []
      };

      // Normalize storage entities
      normalizedUsage.storage.list = storageEntityOrder.map(({ key, label }) => {
        const entityData = rawStorageByEntity[key] || {};
        const size = entityData.size || entityData.totalSize || 0;
        const count = entityData.count || entityData.fileCount || 0;

        normalizedUsage.storage.byEntity[key] = {
          size,
          totalSize: size,
          count,
          fileCount: count,
          label
        };

        const limit = normalizedUsage.limits.storage.byEntityType?.[key]?.limit || null;
        const percentage = limit ? usageControlService.calculateUsagePercentage(size, limit) : 0;

        return {
          key,
          label,
          size,
          count,
          limit,
          percentage
        };
      });

      // Normalize entity counts
      normalizedUsage.entities.list = entityRecordOrder.map(({ key, label }) => {
        const count = rawEntityCounts[key] || 0;
        normalizedUsage.entities.byType[key] = count;

        const limit = normalizedUsage.limits.entities?.[key]?.limit || null;
        const percentage = limit ? usageControlService.calculateUsagePercentage(count, limit) : 0;

        return {
          key,
          label,
          count,
          limit,
          percentage
        };
      });
      
      setUsageData(normalizedUsage);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const toggleUsageView = () => {
    if (!showUsage && !usageData) {
      loadUsageData();
    }
    setShowUsage(!showUsage);
  };


  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <Link 
            to="/dashboard" 
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>{t('auth.backToDashboard')}</span>
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">{t('auth.userProfile')}</h1>
        <p className="text-gray-600 mt-2">{t('auth.manageAccount')}</p>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('auth.profileInfo')}</h2>
              <p className="text-gray-600">{t('auth.updatePersonalDetails')}</p>
            </div>
          </div>

          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.firstName')} *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={profileForm.firstName}
                  onChange={handleProfileChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.firstName')}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.firstName}</span>
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  {t('auth.lastName')} *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={profileForm.lastName}
                  onChange={handleProfileChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.lastName')}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{errors.lastName}</span>
                  </p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={profileForm.email}
                  readOnly
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                  placeholder={t('auth.email')}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {t('auth.emailNotEditable')}
              </p>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.username')}
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={profileForm.username}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                placeholder={t('auth.username')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('auth.usernameNotEditable')}
              </p>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save className="h-4 w-4" />
                )}
                <span>{isLoading ? t('auth.updating') : t('auth.updateProfile')}</span>
              </button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="h-10 w-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center">
              <Lock className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{t('auth.changePassword')}</h2>
              <p className="text-gray-600">{t('auth.updateAccountPassword')}</p>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.currentPassword')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordErrors.currentPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.currentPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.currentPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>{passwordErrors.currentPassword}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.newPassword')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordErrors.newPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.newPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>{passwordErrors.newPassword}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                {t('auth.confirmNewPassword')} *
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full pl-10 pr-10 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    passwordErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder={t('auth.confirmNewPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <AlertCircle className="h-4 w-4" />
                  <span>{passwordErrors.confirmPassword}</span>
                </p>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isPasswordLoading}
                className="w-full flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPasswordLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Lock className="h-4 w-4" />
                )}
                <span>{isPasswordLoading ? t('auth.changing') : t('auth.changePassword')}</span>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Account Information */}
      <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('auth.accountInfo')}</h3>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleTestCentralDb}
              className="flex items-center space-x-2 text-sm text-green-600 hover:text-green-800 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Test Central DB</span>
            </button>
            <button
              onClick={() => window.location.reload()}
              className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{t('auth.refreshData')}</span>
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Account Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">{t('auth.basicInfo')}</h4>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.userID')}</p>
              <div className="space-y-1">
                <div>
                  <span className="text-xs text-gray-500">Central: </span>
                  <span className="text-sm text-gray-900 font-mono">{user.userId}</span>
                </div>
                {user.tenantUserId && (
                  <div>
                    <span className="text-xs text-gray-500">Tenant: </span>
                    <span className="text-sm text-gray-900 font-mono">{user.tenantUserId}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.userType')}</p>
              <p className="text-sm text-gray-900 capitalize">
                {user.userType === 'admin' ? t('auth.administrator') : t('auth.regularUser')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.accountStatus')}</p>
              <p className="text-sm text-gray-900">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive !== false ? t('auth.active') : t('auth.inactive')}
                </span>
              </p>
            </div>
            {user.phone && (
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.phone')}</p>
                <p className="text-sm text-gray-900">{user.phone}</p>
              </div>
            )}
            {user.department && (
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.department')}</p>
                <p className="text-sm text-gray-900">{user.department}</p>
              </div>
            )}
            {user.jobTitle && (
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.jobTitle')}</p>
                <p className="text-sm text-gray-900">{user.jobTitle}</p>
              </div>
            )}
          </div>

          {/* Roles & Permissions */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">{t('auth.rolesPermissions')}</h4>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.roles')}</p>
              <button
                onClick={() => {
                  // Check if user has multiple roles
                  const allRoles = user?.roles || [];
                  const availableRoles = allRoles
                    .map(role => {
                      const roleName = role.RoleName || role;
                      return typeof roleName === 'string' ? roleName : String(roleName);
                    })
                    .filter((roleName, index, self) => self.indexOf(roleName) === index);
                  
                  if (availableRoles.length > 1) {
                    setIsRoleModalOpen(true);
                  }
                }}
                className={`text-sm text-gray-900 ${
                  (user?.roles || []).length > 1 
                    ? 'hover:text-teal-600 dark:hover:text-teal-400 cursor-pointer transition-colors' 
                    : ''
                }`}
                title={(user?.roles || []).length > 1 ? 'Click to switch role' : ''}
              >
                {getUserRoles()}
              </button>
            </div>
            {user.isAdmin && (
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.adminLevel')}</p>
                <p className="text-sm text-gray-900">
                  {user.isSuperAdmin ? t('auth.superAdmin') : t('auth.admin')}
                </p>
              </div>
            )}
            {user.repositories && user.repositories.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.assignedRepositories')}</p>
                <p className="text-sm text-gray-900">{user.repositories.length} {t('auth.repositories')}</p>
              </div>
            )}
          </div>

          {/* Timestamps */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">{t('auth.timestamps')}</h4>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.accountCreated')}</p>
              <p className="text-sm text-gray-900">
                {user.createdAt ? new Date(user.createdAt).toLocaleString() : t('auth.notAvailable')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.lastUpdated')}</p>
              <p className="text-sm text-gray-900">
                {user.updatedAt ? new Date(user.updatedAt).toLocaleString() : t('auth.notAvailable')}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">{t('auth.lastLogin')}</p>
              <p className="text-sm text-gray-900">
                {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : t('auth.notAvailable')}
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info for Admin Users */}
        {user.userType === 'admin' && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-4">{t('auth.adminInfo')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.adminRole')}</p>
                <p className="text-sm text-gray-900">{user.role || t('auth.notSpecified')}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">{t('auth.permissions')}</p>
                <p className="text-sm text-gray-900">
                  {user.isSuperAdmin ? t('auth.fullAccess') : t('auth.limitedAccess')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Resource Usage Section */}
      {user.tenantCode && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 bg-gradient-to-r from-green-500 to-teal-600 rounded-full flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Resource Usage</h3>
                  <p className="text-sm text-gray-600">
                    Current Tenant: <span className="font-semibold text-blue-600">{user.tenantCode}</span>
                    {user.tenantName && <span className="text-gray-500"> ({user.tenantName})</span>}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Monitor storage, records, and AI usage for this tenant</p>
                </div>
              </div>
              <button
                onClick={toggleUsageView}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {showUsage ? (
                  <>
                    <Eye className="h-4 w-4" />
                    <span>Hide Usage</span>
                  </>
                ) : (
                  <>
                    <BarChart3 className="h-4 w-4" />
                    <span>View Usage</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {showUsage && (
            <div className="p-6">
              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : usageData ? (
                <div className="space-y-6">
                  {/* Usage Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Storage Usage */}
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center justify-between mb-4">
                        <HardDrive className="h-8 w-8 text-blue-600" />
                        {usageData.limits?.storage?.maxTotalSize && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.storage?.totalSize || 0,
                                usageData.limits.storage.maxTotalSize
                              )
                            ) === 'exceeded' ? 'bg-red-100 text-red-700' :
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.storage?.totalSize || 0,
                                usageData.limits.storage.maxTotalSize
                              )
                            ) === 'critical' ? 'bg-orange-100 text-orange-700' :
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.storage?.totalSize || 0,
                                usageData.limits.storage.maxTotalSize
                              )
                            ) === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {usageControlService.calculateUsagePercentage(
                              usageData.storage?.totalSize || 0,
                              usageData.limits.storage.maxTotalSize
                            )}%
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">Storage Usage</h4>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-2xl font-bold text-blue-900">
                            {usageControlService.formatBytes(usageData.storage?.totalSize || 0)}
                          </span>
                          {usageData.limits?.storage?.maxTotalSize && (
                            <span className="text-sm font-medium text-blue-700">
                              / {usageControlService.formatBytes(usageData.limits.storage.maxTotalSize)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-medium text-blue-700">
                          {usageData.storage?.totalFiles || 0} files
                        </p>
                      </div>
                      {usageData.limits?.storage?.maxTotalSize && (
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.storage?.totalSize || 0,
                                    usageData.limits.storage.maxTotalSize
                                  )
                                ) === 'exceeded' ? 'bg-red-600' :
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.storage?.totalSize || 0,
                                    usageData.limits.storage.maxTotalSize
                                  )
                                ) === 'critical' ? 'bg-orange-600' :
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.storage?.totalSize || 0,
                                    usageData.limits.storage.maxTotalSize
                                  )
                                ) === 'warning' ? 'bg-yellow-600' :
                                'bg-green-600'
                              }`}
                              style={{
                                width: `${Math.min(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.storage?.totalSize || 0,
                                    usageData.limits.storage.maxTotalSize
                                  ),
                                  100
                                )}%`
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Total Records */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200">
                      <div className="flex items-center justify-between mb-4">
                        <Database className="h-8 w-8 text-purple-600" />
                      </div>
                      <h4 className="text-sm font-semibold text-purple-900 mb-2">Total Records</h4>
                      <div className="space-y-1">
                        <div className="text-2xl font-bold text-purple-900">
                          {usageControlService.formatNumber(usageData.entities?.total || 0)}
                        </div>
                        <p className="text-xs font-medium text-purple-700">
                          Across all entities
                        </p>
                      </div>
                      <div className="mt-4 space-y-1">
                        {usageData.entities?.list?.map(({ key, label, count, limit }) => (
                          <div key={key} className="flex justify-between text-xs">
                            <span className="font-medium text-purple-700">{label}:</span>
                            <div className="text-right">
                              <span className="font-semibold text-purple-900">
                                {usageControlService.formatNumber(count || 0)}
                              </span>
                              {limit ? (
                                <span className="ml-1 text-[11px] font-medium text-purple-600">
                                  / {usageControlService.formatNumber(limit)}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* AI Token Usage */}
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border border-green-200">
                      <div className="flex items-center justify-between mb-4">
                        <Zap className="h-8 w-8 text-green-600" />
                        {usageData.limits?.aiTokens?.monthlyLimit && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.aiTokens?.monthlyTokens || 0,
                                usageData.limits.aiTokens.monthlyLimit
                              )
                            ) === 'exceeded' ? 'bg-red-100 text-red-700' :
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.aiTokens?.monthlyTokens || 0,
                                usageData.limits.aiTokens.monthlyLimit
                              )
                            ) === 'critical' ? 'bg-orange-100 text-orange-700' :
                            usageControlService.getUsageStatus(
                              usageControlService.calculateUsagePercentage(
                                usageData.aiTokens?.monthlyTokens || 0,
                                usageData.limits.aiTokens.monthlyLimit
                              )
                            ) === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {usageControlService.calculateUsagePercentage(
                              usageData.aiTokens?.monthlyTokens || 0,
                              usageData.limits.aiTokens.monthlyLimit
                            )}%
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold text-green-900 mb-2">AI Tokens (Monthly)</h4>
                      <div className="space-y-1">
                        <div className="flex items-baseline justify-between">
                          <span className="text-2xl font-bold text-green-900">
                            {usageControlService.formatNumber(usageData.aiTokens?.monthlyTokens || 0)}
                          </span>
                          {usageData.limits?.aiTokens?.monthlyLimit && (
                            <span className="text-sm font-medium text-green-700">
                              / {usageControlService.formatNumber(usageData.limits.aiTokens.monthlyLimit)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs font-medium text-green-700">
                          {usageData.aiTokens?.totalRequests || 0} requests
                        </p>
                      </div>
                      {usageData.limits?.aiTokens?.monthlyLimit && (
                        <div className="mt-4">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.aiTokens?.monthlyTokens || 0,
                                    usageData.limits.aiTokens.monthlyLimit
                                  )
                                ) === 'exceeded' ? 'bg-red-600' :
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.aiTokens?.monthlyTokens || 0,
                                    usageData.limits.aiTokens.monthlyLimit
                                  )
                                ) === 'critical' ? 'bg-orange-600' :
                                usageControlService.getUsageStatus(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.aiTokens?.monthlyTokens || 0,
                                    usageData.limits.aiTokens.monthlyLimit
                                  )
                                ) === 'warning' ? 'bg-yellow-600' :
                                'bg-green-600'
                              }`}
                              style={{
                                width: `${Math.min(
                                  usageControlService.calculateUsagePercentage(
                                    usageData.aiTokens?.monthlyTokens || 0,
                                    usageData.limits.aiTokens.monthlyLimit
                                  ),
                                  100
                                )}%`
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Alerts */}
                  {usageData.alerts && usageData.alerts.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <h4 className="text-sm font-semibold text-yellow-900 mb-2">Usage Alerts</h4>
                          <ul className="space-y-1">
                            {usageData.alerts.map((alert, index) => (
                              <li key={index} className="text-sm text-yellow-800">
                                • {alert.message}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Detailed Breakdown */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Storage by Entity Type */}
                    {usageData.storage?.list && usageData.storage.list.length > 0 && (
                      <div className="bg-white rounded-lg p-5 border-2 border-gray-300 shadow-sm">
                        <h4 className="text-base font-bold text-gray-900 mb-4">Storage by Entity Type</h4>
                        <div className="space-y-4">
                          {usageData.storage.list.map((entity) => {
                            const { key, label, size, count, limit, percentage } = entity;
                            return (
                              <div key={key} className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-bold text-gray-900">{label}</span>
                                  <div className="text-right">
                                    <div className="text-sm font-bold text-gray-900">
                                      {usageControlService.formatBytes(size)}
                                    </div>
                                    <div className="text-xs font-semibold text-gray-800">
                                      {count} files
                                    </div>
                                  </div>
                                </div>
                                {limit && (
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 bg-gray-300 rounded-full h-2.5">
                                      <div
                                        className={`h-2.5 rounded-full ${
                                          percentage >= 95 ? 'bg-red-600' :
                                          percentage >= 80 ? 'bg-yellow-600' :
                                          'bg-green-600'
                                        }`}
                                        style={{ width: `${Math.min(percentage, 100)}%` }}
                                      />
                                    </div>
                                    <span className="text-xs font-bold text-gray-900 w-12 text-right">{percentage}%</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* AI Usage by Service */}
                    {usageData.aiTokens?.byService && Object.keys(usageData.aiTokens.byService).length > 0 && (
                      <div className="bg-white rounded-lg p-5 border-2 border-gray-300 shadow-sm">
                        <h4 className="text-base font-bold text-gray-900 mb-4">AI Usage by Service</h4>
                        <div className="space-y-4">
                          {Object.entries(usageData.aiTokens.byService).map(([service, data]) => (
                            <div key={service} className="flex items-center justify-between">
                              <span className="text-sm font-bold text-gray-900 capitalize">{service}</span>
                              <div className="text-right">
                                <div className="text-sm font-bold text-gray-900">
                                  {usageControlService.formatNumber(data?.tokens || data?.totalTokens || 0)} tokens
                                </div>
                                <div className="text-xs font-semibold text-gray-800">
                                  {data?.requests || data?.requestCount || 0} requests
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Last Updated */}
                  <div className="text-center text-xs font-medium text-gray-700">
                    Last updated: {new Date(usageData.timestamp).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No usage data available</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Google Calendar Settings */}
      <div className="mt-6">
        <GoogleCalendarSettings />
      </div>

      {/* Role Selector Modal */}
      <RoleSelectorModal 
        isOpen={isRoleModalOpen} 
        onClose={() => setIsRoleModalOpen(false)} 
      />
    </div>
  );
};

export default UserProfile; 